import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';

import { setLanguage } from '../store/slices/userSlice';
import { userInfo } from '../store/selectors/user';
import { userAPI } from '../modules/profile/api/userAPI';
import { notificationOccurred, selectionChanged } from '../utils/hapticEvents';

export const APP_LANGUAGES = ['ru', 'uz', 'en'] as const;
export type AppLanguage = (typeof APP_LANGUAGES)[number];

const LANGUAGE_KEY = 'language';
// Флаг «язык выбран осознанно»: без него главная при первом входе открывает
// шторку выбора — см. Home.
const CHOSEN_KEY = 'hasChosenLanguage';

export const readAppLanguage = (): AppLanguage => {
  const stored = localStorage.getItem(LANGUAGE_KEY);

  return (APP_LANGUAGES as readonly string[]).includes(stored ?? '')
    ? (stored as AppLanguage)
    : 'uz';
};

/**
 * Единственное место, где меняется язык приложения.
 *
 * Шторка в настройках, шторка первого входа и переключатель в шапке делают
 * одно и то же: стор, localStorage, i18n и перезапрос данных с бэкенда.
 * Раньше первые две дублировали друг друга построчно, и
 * любое дополнение пришлось бы вносить дважды.
 *
 * Возвращает true, если язык действительно сменился. Повторный выбор
 * текущего — no-op: никаких перезапросов.
 */
export const useChangeLanguage = () => {
  const dispatch = useDispatch();
  const { i18n } = useTranslation();
  const queryClient = useQueryClient();
  const user = useSelector(userInfo);

  return useCallback(
    (next: AppLanguage): boolean => {
      const previous = readAppLanguage();

      // Даже без смены языка — выбор состоялся, шторку первого входа больше
      // не показываем.
      localStorage.setItem(CHOSEN_KEY, 'true');

      if (next === previous && i18n.language === next) {
        return false;
      }

      dispatch(setLanguage(next));
      localStorage.setItem(LANGUAGE_KEY, next);
      i18n.changeLanguage(next);
      notificationOccurred('success');
      selectionChanged();

      // Меню, баннеры, филиалы и прочее приходят с бэкенда уже на нужном
      // языке: заголовок Language читается из localStorage в момент запроса,
      // поэтому достаточно перезапросить всё активное. Стор, корзина и
      // сессия при этом не трогаются.
      queryClient.invalidateQueries();

      // Язык сохраняем в профиле — дальше его забирает бэкенд (в том числе
      // для Mindbox). Только для загруженного профиля: до загрузки в name
      // заглушка 'EVOS', и она затёрла бы настоящее имя. Сюда можно попасть
      // и без смены сохранённого языка — если i18n разошёлся с localStorage;
      // тогда сохранять нечего.
      if (next !== previous && user.isAuth && user._id && user.name) {
        userAPI.saveLanguage(user.name, next).catch(error => {
          console.error('Не удалось сохранить язык в профиле', error);
        });
      }

      return true;
    },
    [dispatch, i18n, queryClient, user.isAuth, user._id, user.name]
  );
};
